import { useMemo } from 'react';
import { Link, Navigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { Plus, RefreshCw, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils/cn';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { useDialers } from '../api';
import type { DialerRow } from '../types';
import { useModuleFilter } from '@/lib/permissions/use-module-filter';
import { BASE_PERMISSION, MODULE_LISTING_KEYS } from '@/lib/constants/modules';
import { usePermission } from '@/lib/permissions/use-permission';


const POLL_MS = 45_000;

export function DialerListPage() {
  const query = useDialers({ refetchIntervalMs: POLL_MS });
  const rows = useModuleFilter(query.data ?? [], MODULE_LISTING_KEYS.MANAGE_DIALERS, (d) => d.dialerId);
  const canSearch = usePermission('Manage Dialers.Search Dialer', BASE_PERMISSION.VIEW);
  const canViewUsage = usePermission('Manage Dialers.Dialer Usage', BASE_PERMISSION.VIEW);
  const canCreate = usePermission('Manage Dialers.Create Dialer', BASE_PERMISSION.ADD);

  // Edit Dialer is intentionally never offered from this list, regardless of ACL --
  // dialer config is never user-editable from here (matches the legacy panel).
  const columns = useMemo<ColumnDef<DialerRow>[]>(
    () => [
      {
        accessorKey: 'dialerId',
        header: 'ID',
        size: 80,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">
            {row.original.dialerId}
          </span>
        ),
      },
      {
        accessorKey: 'dialerName',
        header: 'Dialer name',
        cell: ({ row }) => (
          <span className="font-medium text-foreground">
            {row.original.dialerName}
          </span>
        ),
      },
      { accessorKey: 'dialerIP', header: 'IP', size: 130 },
      { accessorKey: 'dialerPort', header: 'Port', size: 80 },
      { accessorKey: 'pilotNumber', header: 'Pilot No.', size: 130 },
      { accessorKey: 'activeCli', header: 'Active CLI', size: 110 },
      { accessorKey: 'dialerCalls', header: 'Active calls', size: 110 },
      { accessorKey: 'totalChannels', header: 'Total channels', size: 130 },
      { accessorKey: 'channelReduction', header: 'Channel reduction', size: 150 },
      { accessorKey: 'cpsReduction', header: 'CPS reduction', size: 130 },
    ],
    [],
  );

  const canView = usePermission('Manage Dialers.Dialer List', BASE_PERMISSION.VIEW);

  if (!canView) return <Navigate to="/" replace />;

  return (
    <FillPage>
      <PageHeader
        title="All Dialers"
        description="Live view of the dialer fleet. Refreshes automatically every 45 seconds."
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={() => query.refetch()}
              disabled={query.isFetching}
              aria-label="Refresh"
              title="Refresh"
            >
              <RefreshCw
                className={cn('h-4 w-4', query.isFetching && 'animate-spin')}
              />
            </Button>
            {canSearch && (
              <Button asChild variant="outline">
                <Link to="/search-dialer">
                  <Search className="h-4 w-4" />
                  Search by dialer
                </Link>
              </Button>
            )}
            {canViewUsage && (
              <Button asChild variant="outline">
                <Link to="/dialer-usage">Usage</Link>
              </Button>
            )}
            {canCreate && (
              <Button asChild>
                <Link to="/create-dialer">
                  <Plus className="h-4 w-4" />
                  Add dialer
                </Link>
              </Button>
            )}
          </div>
        }
      />
      <DataTable<DialerRow, unknown>
        tableId="dialers:list:v2"
        columns={columns}
        data={rows}
        isLoading={query.isLoading}
        searchPlaceholder="Search dialers…"
        emptyTitle="No dialers found"
        emptyDescription="Add a dialer to start routing calls."
        getRowId={(row) => String(row.dialerId)}
        exportFilename="dialers.csv"
        pageSize={10}
      />
    </FillPage>
  );
}